import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { LinkIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import { OAuthGrant } from '@/lib/types';
import {
  listOAuthGrants,
  mcpEndpointURL,
  revokeOAuthGrant,
  scopeLabels,
} from '@/utils/oauth-connections';

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : 'never');

// ConnectedApps lists the assistants (Claude, ChatGPT, …) the current user has
// connected through the MCP server and lets them disconnect. Connections are
// per user, not per workspace: revoking here ends the app's access to every
// workspace the user belongs to.
export default function ConnectedApps() {
  const [grants, setGrants] = useState<OAuthGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const mcpUrl = mcpEndpointURL();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setGrants(await listOAuthGrants());
    } catch (e: any) {
      toaster.create({
        type: 'error',
        title: e?.response?.data?.error || 'Could not load connected apps',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const disconnect = async (grant: OAuthGrant) => {
    setBusy(grant.id);
    try {
      await revokeOAuthGrant(grant.id);
      setGrants((prev) => prev.filter((g) => g.id !== grant.id));
      toaster.create({ type: 'success', title: `Disconnected ${grant.client_host}` });
    } catch (e: any) {
      toaster.create({ type: 'error', title: e?.response?.data?.error || 'Could not disconnect' });
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toaster.create({
        type: 'error',
        title: 'Could not copy — select the URL and copy it manually',
      });
    }
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl'>
        <Card.Header>
          <Container maxW='5xl'>
            <Stack direction='row' align='center' mb={2}>
              <Icon as={LinkIcon} w={7} h={7} color='brand.solid' />
              <Heading size='3xl'>Connected apps</Heading>
            </Stack>
            <Text color='fg.muted'>
              AI assistants such as Claude and ChatGPT that can work with your Athlete Era data on
              your behalf. Each one acts as you, with your role in each workspace.
            </Text>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW='5xl'>
            <Stack gap={8}>
              <Box borderWidth={1} borderColor='border' borderRadius='lg' p={5} bg='bg.subtle'>
                <Stack gap={3}>
                  <Heading size='md'>Connect an assistant</Heading>
                  <Text fontSize='sm' color='fg.muted'>
                    Add this URL as a custom connector (Claude: Settings → Connectors → Add custom
                    connector; ChatGPT: Settings → Apps → Developer mode). You will be asked to sign
                    in and approve access, and you can disconnect here at any time.
                  </Text>
                  <HStack>
                    <Code px={3} py={2} fontSize='sm' flex={1} overflowX='auto'>
                      {mcpUrl}
                    </Code>
                    <Button size='sm' variant='outline' onClick={copyUrl}>
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </HStack>
                </Stack>
              </Box>

              {loading ? (
                <Flex justify='center' py={10}>
                  <Spinner size='lg' />
                </Flex>
              ) : grants.length === 0 ? (
                <Text color='fg.muted' textAlign='center' py={6}>
                  No apps are connected yet.
                </Text>
              ) : (
                <Stack gap={4}>
                  {grants.map((g) => (
                    <Box key={g.id} borderWidth={1} borderColor='border' borderRadius='lg' p={5}>
                      <Flex align='start' gap={4} direction={{ base: 'column', md: 'row' }}>
                        <Stack gap={2} flex={1}>
                          <HStack>
                            <Text fontSize='lg' fontWeight='600' color='fg.strong'>
                              {g.client_host}
                            </Text>
                            {g.verified ? (
                              <Badge colorPalette='green'>Verified</Badge>
                            ) : (
                              <Badge colorPalette='orange'>Unverified</Badge>
                            )}
                          </HStack>
                          {g.client_name && g.client_name !== g.client_host && (
                            <Text fontSize='sm' color='fg.muted'>
                              {g.client_name}
                            </Text>
                          )}
                          <HStack flexWrap='wrap' gap={2}>
                            {g.scopes.map((s) => (
                              <Badge
                                key={s}
                                variant='subtle'
                                colorPalette={s.startsWith('write:') ? 'orange' : 'gray'}
                              >
                                {scopeLabels[s] ?? s}
                              </Badge>
                            ))}
                          </HStack>
                          <Text fontSize='sm' color='fg.muted'>
                            Default workspace:{' '}
                            {g.default_workspace_name || `#${g.default_workspace_id}`} · Connected{' '}
                            {formatDate(g.last_authorized_at)} · Last used{' '}
                            {formatDate(g.last_used_at)}
                          </Text>
                        </Stack>
                        <HStack>
                          {confirming === g.id ? (
                            <>
                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={() => setConfirming(null)}
                                disabled={busy === g.id}
                              >
                                Cancel
                              </Button>
                              <Button
                                size='sm'
                                colorPalette='red'
                                onClick={() => disconnect(g)}
                                loading={busy === g.id}
                                loadingText='Disconnecting…'
                              >
                                Confirm disconnect
                              </Button>
                            </>
                          ) : (
                            <Button
                              size='sm'
                              variant='outline'
                              colorPalette='red'
                              onClick={() => setConfirming(g.id)}
                            >
                              Disconnect
                            </Button>
                          )}
                        </HStack>
                      </Flex>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
